import { Component, OnChanges, OnInit, SimpleChanges, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {StringUtils} from '../../utils/string-utils';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {SelectionSystemType} from '../../model/selection-system-type';
import {SureFlowLoopDetail} from '../../model/sure-flow-loop-detail';
import {SureFlowLoopInitial} from '../../model/sure-flow-loop-initial';
import {DialogService} from '../../services/dialog.service';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {SureFlowAutoSelectInput} from '../../model/sure-flow-auto-select-input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SureflowLoopDetailComponent } from '../sureflow-loop-detail/sureflow-loop-detail.component';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem } from '@angular/material/list';

import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-sureflow-loop-designer',
    templateUrl: './sureflow-loop-designer.component.html',
    styleUrls: ['./sureflow-loop-designer.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatList, MatListItem, MatIcon, FormsModule, SureflowLoopDetailComponent, MatProgressSpinner]
})
export class SureflowLoopDesignerComponent implements OnInit, OnChanges {
  private readonly unitSelectionService = inject(UnitSelectionService);
  private readonly dialogService = inject(DialogService);
  readonly loops = input.required<SureFlowLoopDetail[]>();
  readonly showCreateScheduleButton = input.required<boolean>();
  readonly units = input.required<WorkingSureFlowUnit[]>();
  readonly selectedLoopId = input.required<number>();
  readonly invalidUnitIds = input<number[]>(undefined!);
  readonly selectedUnitIds = input.required<number[]>();
  readonly loopSelected = output<SureFlowLoopDetail>();
  readonly addRequested = output<SureFlowLoopInitial>();
  readonly deleteRequested = output<SureFlowLoopDetail>();
  readonly addLoopUnit = output<SureFlowLoopDetail>();
  readonly editSelectedUnits = output<void>();
  readonly deleteLoopUnits = output<void>();
  readonly copyUnits = output<void>();
  readonly addNonSureFlowUnit = output<SureFlowLoopDetail>();
  readonly loopRecalculated = output<SureFlowLoopDetail>();
  readonly autoSelect = output<SureFlowAutoSelectInput>();
  readonly unitsRearranged = output<SureFlowLoopDetail>();
  readonly loopCopied = output<SureFlowLoopDetail>();
  readonly addLoopsToSchedule = output<void>();
  readonly loopDescriptionChanged = output<SureFlowLoopDetail>();
  readonly selectedUnitsChanged = output<WorkingSureFlowUnit[]>();
  readonly exportRequested = output<void>();
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly systemTypes = signal<SelectionSystemType[]>(undefined!);
  selectedLoop!: SureFlowLoopDetail;
  newLoopDescription!: string;

  ngOnInit(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.unitSelectionService.getSureFlowSystemTypes().subscribe({
      next: (systemTypes) => {
        this.systemTypes.set(systemTypes);
        this.isBusy.set(false);
      }, error: (error) => this.handleError(error)
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.selectedLoopId() > 0) {
      this.selectedLoop = this.loops().filter(loop => loop.id === this.selectedLoopId())[0] || undefined;
    } else {
      this.selectedLoop = undefined!;
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  getSelectedSystemType(): SelectionSystemType {
    if (StringUtils.isNotBlank(this.selectedLoop?.systemTypeCode) && (this.systemTypes()?.length || 0) > 0) {
      const filteredTypes: SelectionSystemType[] = this.systemTypes().filter(st => st.code === this.selectedLoop.systemTypeCode);
      if (filteredTypes.length === 1) {
        return JSON.parse(JSON.stringify(filteredTypes[0]));
      }
    }
    return undefined!;
  }

  selectLoop(loop: SureFlowLoopDetail): void {
    this.loopSelected.emit(loop);
  }

  isSelected(loop: SureFlowLoopDetail): boolean {
    return (this.selectedLoop?.id || 0) === loop.id;
  }

  addLoop(): void {
    this.errorMessage.set(undefined!);
    this.dialogService.showAddSureFlowLoopDialog(this.systemTypes()).subscribe({
      next: (input: SureFlowLoopInitial) => {
        if (StringUtils.isNotBlank(input?.systemTypeCode)) {
          this.addRequested.emit(input);
        }
      }, error: (error) => this.handleError(error)
    });
  }

  deleteLoop(): void {
    this.errorMessage.set(undefined!);
    if (this.selectedLoop) {
      this.deleteRequested.emit(this.selectedLoop);
    }
  }

  addUnitToLoop(detail: SureFlowLoopDetail): void {
    this.addLoopUnit.emit(detail);
  }

  unitSelectionChange(units: WorkingSureFlowUnit[]): void {
    this.selectedUnitsChanged.emit(units);
  }

  editUnits(): void {
    this.editSelectedUnits.emit();
  }

  deleteUnits(): void {
    this.deleteLoopUnits.emit();
  }

  copyLoopUnits(): void {
    this.copyUnits.emit();
  }

  addNonSFUnit(detail: SureFlowLoopDetail): void {
    this.addNonSureFlowUnit.emit(detail);
  }

  recalculateLoop(loopDetail: SureFlowLoopDetail): void {
    this.loopRecalculated.emit(loopDetail);
  }

  autoSelectUnits(detail: SureFlowAutoSelectInput): void {
    this.autoSelect.emit(detail);
  }

  rearrangeUnits(detail: SureFlowLoopDetail): void {
    this.unitsRearranged.emit(detail);
  }

  copyLoop(): void {
    this.errorMessage.set(undefined!);
    if (this.selectedLoop) {
      this.loopCopied.emit(this.selectedLoop);
    }
  }

  addToSchedule(): void {
    this.addLoopsToSchedule.emit();
  }

  saveLoopDescription(loop: SureFlowLoopDetail): void {
    this.errorMessage.set(undefined!);
    if ((this.newLoopDescription?.length || 0) === 0) {
      this.errorMessage.set('Please enter a loop description');
    } else {
      loop.description = this.newLoopDescription;
      this.loopDescriptionChanged.emit(loop);
    }
  }

  cancelLoopUpdate(loop: SureFlowLoopDetail): void {
    loop.isEditMode = false;
    this.newLoopDescription = loop.description;
    this.errorMessage.set(undefined!);
  }

  showExportButton(): boolean {
    return (this.loops()?.length || 0) > 0;
  }

  exportLoopData(): void {
    this.exportRequested.emit();
  }

}
